import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

import { LuLayoutDashboard, LuMail, LuStar, LuBed, LuPenTool, LuBarChart3, LuLogOut } from 'react-icons/lu';

const adminNav = [
  { label: 'Dashboard', path: '/admin', icon: <LuLayoutDashboard size={18} /> },
  { label: 'Inquiries', path: '/admin/inquiries', icon: <LuMail size={18} /> },
  { label: 'Reviews', path: '/admin/reviews', icon: <LuStar size={18} /> },
  { label: 'Rooms', path: '/admin/rooms', icon: <LuBed size={18} /> },
  { label: 'Blog', path: '/admin/blog', icon: <LuPenTool size={18} /> },
];

export function AdminLayout({ children, title }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  return (
    <div className="admin-wrapper" style={{ display: 'flex', minHeight: '100vh', background: '#f4f6f4' }}>
      {/* Sidebar */}
      <div className="admin-sidebar" style={{ width: 240, background: 'var(--forest)', display: 'flex', flexDirection: 'column', position: 'fixed', top: 0, bottom: 0, left: 0, zIndex: 100 }}>
        <div className="admin-sidebar-header" style={{ padding: '24px', borderBottom: '1px solid rgba(201,168,76,0.15)', flexShrink: 0, textAlign: 'center' }}>
          <Link to="/">
            <img 
              src="/logo-green.jpeg" 
              alt="Villa Tesori" 
              style={{ height: '56px', width: 'auto', marginBottom: 6 }} 
            />
          </Link>
          <div style={{ fontSize: '0.62rem', color: 'rgba(200,217,192,0.4)', letterSpacing: '0.25em', textTransform: 'uppercase' }}>Administration</div>
        </div>
        <nav className="admin-sidebar-nav" style={{ flex: 1, padding: '16px 0', overflowY: 'auto' }}>
          {adminNav.map(item => (
            <Link key={item.path} to={item.path} style={{
              display: 'flex', alignItems: 'center', gap: 12,
              padding: '12px 24px',
              color: location.pathname === item.path ? 'var(--gold-light)' : 'rgba(200,217,192,0.75)',
              background: location.pathname === item.path ? 'rgba(201,168,76,0.1)' : 'transparent',
              fontFamily: 'Jost, sans-serif', fontSize: '0.88rem', transition: 'all 0.2s',
              borderLeft: location.pathname === item.path ? '2px solid var(--gold)' : '2px solid transparent',
            }}
            onMouseEnter={e => { if (location.pathname !== item.path) { e.currentTarget.style.background = 'rgba(201,168,76,0.08)'; e.currentTarget.style.color = 'var(--gold-light)'; }}}
            onMouseLeave={e => { if (location.pathname !== item.path) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'rgba(200,217,192,0.75)'; }}}
            >
              <span>{item.icon}</span>{item.label}
            </Link>
          ))}
        </nav>
        <div className="admin-sidebar-footer" style={{ padding: '16px 24px', borderTop: '1px solid rgba(201,168,76,0.2)', flexShrink: 0 }}>
          <div style={{ fontSize: '0.82rem', color: 'rgba(200,217,192,0.7)', marginBottom: 8 }}>
            {user?.name}
          </div>
          <button onClick={handleLogout} style={{
            background: 'none', border: '1px solid rgba(201,168,76,0.3)', color: 'rgba(200,217,192,0.6)',
            padding: '8px 16px', cursor: 'pointer', fontFamily: 'Jost, sans-serif', fontSize: '0.78rem',
            borderRadius: 2, width: '100%', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8
          }}>
            <LuLogOut size={14} /> Sign Out
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="admin-content" style={{ flex: 1, marginLeft: 240, padding: '32px 40px', minWidth: 0 }}>
        <h1 className="admin-title" style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '2rem', color: 'var(--forest)', marginBottom: 32 }}>{title}</h1>
        {children}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { data: inquiries } = useQuery({ queryKey: ['admin-inquiries'], queryFn: () => axios.get('/api/inquiries').then(r => r.data).catch(() => []) });
  const { data: reviews } = useQuery({ queryKey: ['admin-reviews'], queryFn: () => axios.get('/api/reviews/admin/all').then(r => r.data).catch(() => []) });
  const { data: blogs } = useQuery({ queryKey: ['admin-blogs'], queryFn: () => axios.get('/api/blog/admin/all').then(r => r.data).catch(() => []) });

  const newInquiries = inquiries?.filter(i => i.status === 'new')?.length || 0;
  const pendingReviews = reviews?.filter(r => !r.isApproved)?.length || 0;
  const publishedPosts = blogs?.filter(b => b.isPublished)?.length || 0;

  const stats = [
    { label: 'New Inquiries', value: newInquiries, icon: <LuMail size={24} />, path: '/admin/inquiries', color: '#e8f4e8' },
    { label: 'Pending Reviews', value: pendingReviews, icon: <LuStar size={24} />, path: '/admin/reviews', color: '#fdf6e3' },
    { label: 'Total Inquiries', value: inquiries?.length || 0, icon: <LuBarChart3 size={24} />, path: '/admin/inquiries', color: '#e8f0f8' },
    { label: 'Blog Posts', value: publishedPosts, icon: <LuPenTool size={24} />, path: '/admin/blog', color: '#f8ece8' },
  ];

  return (
    <>
      <Helmet><title>Admin Dashboard – Villa Tesori</title></Helmet>
      <AdminLayout title="Dashboard Overview">
        <div className="admin-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 20, marginBottom: 40 }}>
          {stats.map(s => (
            <Link key={s.label} to={s.path} style={{
              background: s.color, padding: 'clamp(16px,3vw,24px) clamp(14px,2.5vw,20px)', borderRadius: 8,
              border: '1px solid rgba(0,0,0,0.06)', display: 'block',
            }}>
              <span style={{ fontSize: '1.5rem' }}>{s.icon}</span>
              <div style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(1.8rem,4vw,2.4rem)', color: 'var(--forest)', marginTop: 8 }}>{s.value}</div>
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{s.label}</div>
            </Link>
          ))}
        </div>

        {/* Recent Inquiries */}
        <div style={{ background: 'var(--white)', borderRadius: 8, border: '1px solid #e8e8e8', overflow: 'hidden' }}>
          <div style={{ padding: '20px 24px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
            <h3 style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1.4rem' }}>Recent Inquiries</h3>
            <Link to="/admin/inquiries" style={{ fontSize: '0.8rem', color: 'var(--forest)' }}>View All →</Link>
          </div>
          <div>
            {(inquiries || []).slice(0, 5).map((inq, i) => (
              <div key={inq._id || i} className="admin-row-header" style={{ padding: '16px 24px', borderBottom: '1px solid #f5f5f5', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <div style={{ fontWeight: 500, fontSize: '0.9rem' }}>{inq.name}</div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{inq.email}</div>
                </div>
                <div className="admin-row-actions" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <span style={{
                    padding: '3px 10px', borderRadius: 20, fontSize: '0.72rem',
                    background: inq.status === 'new' ? '#e8f4e8' : '#f5f5f5',
                    color: inq.status === 'new' ? '#2d5a2d' : '#888'
                  }}>{inq.status || 'new'}</span>
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                    {new Date(inq.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            ))}
            {(!inquiries || inquiries.length === 0) && (
              <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-muted)' }}>No inquiries yet</div>
            )}
          </div>
        </div>
      </AdminLayout>
    </>
  );
}
